import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { formatDistanceToNow } from 'date-fns';
import { CornerDownRight, FileText, Paperclip, Plus, Reply as ReplyIcon, Send, Trash2, X } from 'lucide-react';
import CommentReactions from '../../CommentReactions';
import RichCommentEditor, { type RichCommentEditorHandle, type RichCommentEditorPayload } from '../../pm/RichCommentEditor';
import RichCommentRenderer from '../../pm/RichCommentRenderer';
import UserHoverCard from '../../ui/UserHoverCard';
import { useAuth } from '../../../utils/AuthContext';
import { useCRM } from '../../../utils/CRMContext';
import { canDeleteMarketingRequestComment } from '../../../utils/commentPermissions';
import { isInternalCommentMentionUser } from '../../../utils/mentionAffiliation';
import { normalizeCommentReactions, toggleCommentReaction } from '../../../utils/commentReactions';
import { auth, db, storage } from '../../../utils/firebase';
import { buildCommentAttachmentStorageFileName } from '../../../utils/storageFileNames';
import {
  buildTaskCommentsById,
  getTaskCommentThreadRootId,
  groupTaskCommentRepliesByThreadRoot,
} from '../../../utils/taskCommentThread';
import type { CommentAttachment, PdfAnnotation } from '../../../types';
import type { JobApplicationComment } from '../../../types/hiring';
import {
  mapHiringResumePdfThreads,
  type HiringResumePdfFeedItem,
} from '../../../utils/hiringResumeComments';
import { buildHiringResumeDocumentId } from '../../../utils/hiringResumeViewer';
import { subscribeToAnnotationsForDocument, togglePdfAnnotationReaction } from '../../../utils/pdfAnnotationService';
import '../../pm/TaskComments.css';

type Props = {
  applicationId: string;
  applicantName?: string;
  highlightCommentId?: string | null;
  onHighlightConsumed?: () => void;
  highlightPdfCommentId?: string | null;
  onOpenResumePin?: (opts: { annotationId: string; pageNumber?: number }) => void;
};

const draftKey = (uid: string | undefined, applicationId: string) =>
  `jobAppDraft:${uid || 'anon'}:${applicationId}`;

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function mapComment(d: QueryDocumentSnapshot<DocumentData>): JobApplicationComment {
  const data = d.data();
  return {
    id: d.id,
    applicationId: String(data.applicationId || ''),
    userId: String(data.userId || ''),
    userName: String(data.userName || 'Unknown'),
    userInitials: data.userInitials,
    text: String(data.text || ''),
    bodyHtml: data.bodyHtml,
    bodyFormat: data.bodyFormat,
    mentions: data.mentions,
    attachments: data.attachments,
    parentId: data.parentId ?? null,
    reactions: normalizeCommentReactions(data.reactions),
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.(),
  };
}

function asThreadComments(comments: JobApplicationComment[]) {
  return comments.map(c => ({
    id: c.id,
    parentId: c.parentId ?? null,
    createdAt: c.createdAt,
    authorId: c.userId,
    authorName: c.userName,
    body: c.text,
    taskId: c.applicationId,
  }));
}

function excerptOf(comment: JobApplicationComment, max = 80): string {
  const oneLine = (comment.text || '').replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max)}…` : oneLine;
}

const JobApplicationComments: React.FC<Props> = ({
  applicationId,
  applicantName,
  highlightCommentId,
  onHighlightConsumed,
  highlightPdfCommentId,
  onOpenResumePin,
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [comments, setComments] = useState<JobApplicationComment[]>([]);
  const [pdfAnnotations, setPdfAnnotations] = useState<PdfAnnotation[]>([]);
  const [draftHtml, setDraftHtml] = useState('');
  const [initialDraftHtml, setInitialDraftHtml] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [replyTo, setReplyTo] = useState<JobApplicationComment | null>(null);
  const editorRef = useRef<RichCommentEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const mentionableUsers = useMemo(
    () => crmUsers.filter(u =>
      isInternalCommentMentionUser(u)
    ),
    [crmUsers],
  );

  const photoForUid = (uid: string) => crmUsers.find(x => x.uid === uid)?.photoURL;

  const scrollFeedToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!user?.id || !applicationId) return;
    try {
      const saved = window.localStorage.getItem(draftKey(user.id, applicationId));
      if (saved && saved !== '<p></p>') {
        setInitialDraftHtml(saved);
        setDraftHtml(saved);
      } else {
        setInitialDraftHtml('');
        setDraftHtml('');
      }
    } catch {
      /* ignore */
    }
  }, [applicationId, user?.id]);

  useEffect(() => {
    if (!user?.id || !applicationId) return;
    const handle = window.setTimeout(() => {
      try {
        if (draftHtml && draftHtml !== '<p></p>') {
          window.localStorage.setItem(draftKey(user.id, applicationId), draftHtml);
        } else {
          window.localStorage.removeItem(draftKey(user.id, applicationId));
        }
      } catch {
        /* ignore */
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [applicationId, draftHtml, user?.id]);

  useEffect(() => {
    if (!applicationId) return;
    const q = query(
      collection(db, 'jobApplicationComments'),
      where('applicationId', '==', applicationId),
      orderBy('createdAt', 'asc'),
    );
    return onSnapshot(
      q,
      snap => setComments(snap.docs.map(mapComment)),
      err => console.error('[JobApplicationComments] Failed to listen:', err),
    );
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) {
      setPdfAnnotations([]);
      return;
    }
    const documentId = buildHiringResumeDocumentId(applicationId);
    return subscribeToAnnotationsForDocument(documentId, '', setPdfAnnotations);
  }, [applicationId]);

  useLayoutEffect(() => {
    if (highlightCommentId) return;
    scrollFeedToBottom();
  }, [comments.length, highlightCommentId, scrollFeedToBottom]);

  useLayoutEffect(() => {
    if (!highlightCommentId) return;
    const match = comments.find(x => x.id === highlightCommentId);
    if (!match) {
      if (comments.length > 0) onHighlightConsumed?.();
      return;
    }
    const el = document.getElementById(`job-app-comment-${highlightCommentId}`);
    if (!el) {
      onHighlightConsumed?.();
      return;
    }
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('tc-item--flash');
    });
    const t = window.setTimeout(() => {
      el.classList.remove('tc-item--flash');
      onHighlightConsumed?.();
    }, 2200);
    return () => {
      window.clearTimeout(t);
      el.classList.remove('tc-item--flash');
    };
  }, [highlightCommentId, comments, onHighlightConsumed]);

  useLayoutEffect(() => {
    if (!highlightPdfCommentId) return;
    const el = document.getElementById(`job-app-pdf-${highlightPdfCommentId}`);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('tc-item--flash');
    });
    const t = window.setTimeout(() => el.classList.remove('tc-item--flash'), 2200);
    return () => {
      window.clearTimeout(t);
      el.classList.remove('tc-item--flash');
    };
  }, [highlightPdfCommentId, pdfAnnotations]);

  useEffect(() => {
    setReplyTo(null);
  }, [applicationId]);

  const threadComments = useMemo(() => asThreadComments(comments), [comments]);
  const repliesByThreadRoot = useMemo(
    () => groupTaskCommentRepliesByThreadRoot(threadComments as never),
    [threadComments],
  );
  const topLevelComments = useMemo(() => comments.filter(c => !c.parentId), [comments]);
  const pdfThreads = useMemo(() => mapHiringResumePdfThreads(pdfAnnotations), [pdfAnnotations]);
  const mergedRoots = useMemo(() => {
    const team = topLevelComments.map(comment => ({
      kind: 'team' as const,
      createdAt: comment.createdAt,
      comment,
    }));
    const pdf = pdfThreads.map(thread => ({
      kind: 'pdf' as const,
      createdAt: thread.root.createdAt,
      thread,
    }));
    return [...team, ...pdf].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }, [pdfThreads, topLevelComments]);

  const uploadAttachments = useCallback(async (files: File[]): Promise<CommentAttachment[]> => {
    await auth.authStateReady();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || firebaseUser.isAnonymous) {
      throw new Error('Sign in is required to attach files.');
    }
    const uploaded: CommentAttachment[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const fileId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const objectName = buildCommentAttachmentStorageFileName(fileId, file.name);
      const storageRef = ref(storage, `job-application-comments/${applicationId}/${objectName}`);
      await uploadBytes(storageRef, file, {
        contentType: file.type || 'application/octet-stream',
      });
      uploaded.push({
        id: fileId,
        name: file.name,
        url: await getDownloadURL(storageRef),
        type: file.type,
        size: file.size,
      });
      setUploadProgress(((i + 1) / files.length) * 100);
    }
    return uploaded;
  }, [applicationId]);

  const submitNewComment = useCallback(
    async (
      payload?: RichCommentEditorPayload,
      options?: { replyToOverride?: JobApplicationComment | null },
    ) => {
      const editorPayload = payload ?? editorRef.current?.getPayload();
      if (!editorPayload || (editorPayload.isEmpty && attachments.length === 0) || !user || isSubmitting) {
        return;
      }
      setIsSubmitting(true);
      setUploadProgress(0);
      try {
        const uploadedAttachments = attachments.length > 0 ? await uploadAttachments(attachments) : [];
        const userName = crmUsers.find(u => u.uid === user.id)?.displayName || user.name || user.email || 'Unknown';
        const docPayload: Record<string, unknown> = {
          applicationId,
          userId: user.id,
          userName,
          userInitials: getInitials(userName),
          text: editorPayload.bodyPlain,
          bodyHtml: editorPayload.bodyHtml,
          bodyFormat: 'html',
          mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
          attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
          createdAt: Timestamp.now(),
        };
        const effectiveReply = options && 'replyToOverride' in options ? options.replyToOverride : replyTo;
        if (effectiveReply?.id) {
          const byId = buildTaskCommentsById(threadComments as never);
          docPayload.parentId = getTaskCommentThreadRootId(
            { id: effectiveReply.id, parentId: effectiveReply.parentId ?? null },
            byId,
          ) || effectiveReply.id;
        }
        await addDoc(collection(db, 'jobApplicationComments'), docPayload);
        editorRef.current?.clear();
        setDraftHtml('');
        setInitialDraftHtml('');
        try {
          window.localStorage.removeItem(draftKey(user.id, applicationId));
        } catch {
          /* ignore */
        }
        setAttachments([]);
        setReplyTo(null);
        setUploadProgress(0);
        requestAnimationFrame(() => scrollFeedToBottom());
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        window.alert(`Could not send comment: ${message}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    [applicationId, attachments, crmUsers, isSubmitting, replyTo, scrollFeedToBottom, threadComments, uploadAttachments, user],
  );

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteDoc(doc(db, 'jobApplicationComments', commentId));
    } catch (error) {
      console.error('[JobApplicationComments] delete failed', error);
    }
  };

  const getUserDisplayName = useCallback(
    (id: string) => crmUsers.find(u => u.uid === id)?.displayName || id.slice(0, 8),
    [crmUsers],
  );

  const togglePdfFeedReaction = useCallback(
    async (annotationId: string, replyIndex: number | null, emoji: string) => {
      if (!user?.id) return;
      try {
        await togglePdfAnnotationReaction(annotationId, user.id, emoji, replyIndex);
      } catch (error) {
        console.error('[JobApplicationComments] PDF reaction failed', error);
      }
    },
    [user?.id],
  );

  const renderPdfItem = (item: HiringResumePdfFeedItem, options: { indent?: boolean; idx: number }) => {
    const isOwn = String(item.userId ?? '') === String(user?.id ?? '');
    return (
      <div
        key={`pdf-${item.annotationId}-${item.replyIndex ?? 'root'}`}
        id={item.replyIndex == null ? `job-app-pdf-${item.annotationId}` : undefined}
        className={`tc-item tc-item-pdf ${isOwn ? 'tc-item-own' : ''}${options.indent ? ' tc-item--reply' : ''}`}
        style={{ animationDelay: `${options.idx * 30}ms` }}
      >
        {options.indent && <span className="tc-thread-line" aria-hidden />}
        <div className="tc-avatar tc-avatar-pdf">
          <FileText size={12} />
        </div>
        <div className="tc-item-body">
          <div className="tc-item-meta">
            <span className="tc-item-author">{item.userName}</span>
            {item.pageNumber != null && (
              <span className="tc-item-badge-pdf">Resume p.{item.pageNumber}</span>
            )}
            <span className="tc-item-time">{formatDistanceToNow(item.createdAt, { addSuffix: true })}</span>
          </div>
          <div className="tc-item-text">{item.content}</div>
          {user && item.annotationId && (
            <CommentReactions
              classPrefix="tc-"
              reactions={item.reactions || {}}
              currentUserId={user.id}
              onToggle={emoji => togglePdfFeedReaction(item.annotationId, item.replyIndex, emoji)}
              getUserDisplayName={getUserDisplayName}
              revealOnHover
            />
          )}
          {onOpenResumePin && item.replyIndex == null && (
            <button
              type="button"
              className="tc-pdf-link"
              onClick={() => onOpenResumePin({ annotationId: item.annotationId, pageNumber: item.pageNumber })}
            >
              Open on resume
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCommentItem = (c: JobApplicationComment, options: { indent?: boolean; idx: number }) => {
    const isOwn = String(c.userId ?? '') === String(user?.id ?? '');
    const canModerate = !!user && canDeleteMarketingRequestComment(user, c);
    return (
      <div
        key={c.id}
        id={`job-app-comment-${c.id}`}
        className={`tc-item ${isOwn ? 'tc-item-own' : ''}${options.indent ? ' tc-item--reply' : ''}`}
        style={{ animationDelay: `${options.idx * 30}ms` }}
      >
        {options.indent && <span className="tc-thread-line" aria-hidden />}
        <UserHoverCard
          className="tc-avatar"
          name={c.userName}
          photoURL={photoForUid(c.userId)}
          userId={c.userId}
          crmUser={crmUsers.find(u => u.uid === c.userId)}
        />
        <div className="tc-item-body">
          <div className="tc-item-meta">
            <span className="tc-item-author">{c.userName}</span>
            <span className="tc-item-time">{formatDistanceToNow(c.createdAt, { addSuffix: true })}</span>
            <div className="tc-item-actions">
              {c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn"
                  onClick={() => setReplyTo(c)}
                  title="Reply"
                  aria-label="Reply"
                >
                  <ReplyIcon size={11} />
                </button>
              )}
              {canModerate && c.id && (
                <button
                  type="button"
                  className="tc-item-delete"
                  onClick={() => void handleDelete(c.id)}
                  title={isOwn ? 'Delete your comment' : 'Delete comment (moderator)'}
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          </div>
          <div className="tc-item-text">
            <RichCommentRenderer bodyHtml={c.bodyHtml} body={c.text} />
          </div>
          {user && c.id && (
            <CommentReactions
              classPrefix="tc-"
              reactions={c.reactions || {}}
              currentUserId={user.id}
              onToggle={emoji => toggleCommentReaction(db, doc(db, 'jobApplicationComments', c.id), emoji, user.id)}
              getUserDisplayName={getUserDisplayName}
              revealOnHover
            />
          )}
          {c.attachments && c.attachments.length > 0 && (
            <div className="tc-attachments">
              {c.attachments.map(att => (
                <a
                  key={att.id}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tc-attachment-chip"
                  title={`${att.name} (${formatFileSize(att.size)})`}
                >
                  <span className="tc-attachment-icon">
                    {att.type?.startsWith('image/') ? <Paperclip size={14} /> : <FileText size={14} />}
                  </span>
                  <span className="tc-attachment-name">{att.name}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const sendDisabled = isSubmitting || ((!draftHtml || draftHtml === '<p></p>') && attachments.length === 0);

  return (
    <div className="people-hiring-candidate-comments">
      <h4 className="people-hiring-comments-title">Comments</h4>
      <div className="tc-section">
        <div className="tc-feed" ref={scrollRef}>
          {mergedRoots.length === 0 && (
            <div className="tc-empty">No comments yet. Start the conversation.</div>
          )}
          {mergedRoots.map((item, idx) => {
            if (item.kind === 'pdf') {
              return (
                <React.Fragment key={`pdf-thread-${item.thread.root.annotationId}`}>
                  {renderPdfItem(item.thread.root, { idx })}
                  {item.thread.replies.map((reply, ri) =>
                    renderPdfItem(reply, { indent: true, idx: idx + ri + 1 }),
                  )}
                </React.Fragment>
              );
            }
            const replies = (item.comment.id ? repliesByThreadRoot.get(item.comment.id) || [] : [])
              .map(r => comments.find(x => x.id === r.id))
              .filter((x): x is JobApplicationComment => !!x);
            return (
              <React.Fragment key={item.comment.id}>
                {renderCommentItem(item.comment, { idx })}
                {replies.map((r, ri) => renderCommentItem(r, { indent: true, idx: idx + ri + 1 }))}
              </React.Fragment>
            );
          })}
        </div>
        <form
          className="tc-composer"
          onSubmit={e => {
            e.preventDefault();
            void submitNewComment();
          }}
        >
          {replyTo && (
            <div className="tc-reply-chip">
              <CornerDownRight size={12} />
              <span className="tc-reply-chip-label">Replying to <strong>{replyTo.userName}</strong>:</span>
              <span className="tc-reply-chip-excerpt">{excerptOf(replyTo)}</span>
              <button
                type="button"
                className="tc-reply-chip-clear"
                onClick={() => setReplyTo(null)}
                title="Cancel reply"
                aria-label="Cancel reply"
              >
                <X size={12} />
              </button>
            </div>
          )}
          {attachments.length > 0 && (
            <div className="tc-composer-attachments">
              {attachments.map((f, i) => (
                <div key={`${f.name}-${i}`} className="tc-staged-file">
                  <span className="tc-staged-icon"><Paperclip size={14} /></span>
                  <span className="tc-staged-name">{f.name}</span>
                  <button type="button" className="tc-staged-remove" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="tc-upload-bar">
              <div className="tc-upload-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
          <RichCommentEditor
            ref={editorRef}
            initialHtml={initialDraftHtml}
            mentionableUsers={mentionableUsers}
            taskId={applicationId}
            onChange={setDraftHtml}
            onSubmit={payload => void submitNewComment(payload)}
            placeholder={
              applicantName
                ? `Write a comment on ${applicantName}… (@ to mention)`
                : 'Write a comment… (@ to mention, ⌘/Ctrl-Enter to send)'
            }
            disabled={isSubmitting}
          />
          <div className="tc-composer-row">
            <div className="tc-composer-spacer" />
            <input
              type="file"
              ref={fileInputRef}
              className="tc-file-input"
              multiple
              accept="image/*,application/pdf,.pdf"
              onChange={e => {
                if (e.target.files) setAttachments(prev => [...prev, ...Array.from(e.target.files || [])]);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
            <button
              type="button"
              className="tc-btn-attach"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
              disabled={isSubmitting}
            >
              <Plus size={14} />
            </button>
            <button
              type="submit"
              className="tc-btn-send"
              title={isSubmitting ? 'Sending…' : 'Send (⌘/Ctrl-Enter)'}
              disabled={sendDisabled}
            >
              <Send size={14} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationComments;
